import { useId } from 'react';
import { clampLayoutGrid, layoutBands, type LayoutGridBand, type NoticeLayoutGrid } from './noticeLayoutGrid';

/** 대지 위 도움선 — 화면 픽셀로 그려 선명하고, 포인터는 통과시킨다. 썸네일·재생 화면에는 없다. */
export function LayoutGridOverlay({ grid, width, height, scale }: { grid: NoticeLayoutGrid; width: number; height: number; scale: number }) {
  const pattern = `layout-grid-${useId().replace(/[^\w-]/g, '')}`, size = grid.square.size * scale;
  return (
    <svg aria-hidden data-layout-grid className="pointer-events-none absolute inset-0" width={width * scale} height={height * scale}>
      {grid.square.on && size >= 3 && <>
        <defs><pattern id={pattern} width={size} height={size} patternUnits="userSpaceOnUse">
          <path d={`M ${size} 0 L 0 0 0 ${size}`} fill="none" stroke="rgba(56,189,248,0.35)" strokeWidth={1} />
        </pattern></defs>
        <rect width="100%" height="100%" fill={`url(#${pattern})`} />
      </>}
      {/* 열·행은 같은 색 — 둘 다 켜면 겹친 칸이 진해져 모듈로 읽힌다. */}
      {layoutBands(grid.columns, width).map((band, index) =>
        <rect key={`c${index}`} x={band.start * scale} y={0} width={band.size * scale} height={height * scale} fill="rgba(248,113,113,0.12)" />)}
      {layoutBands(grid.rows, height).map((band, index) =>
        <rect key={`r${index}`} x={0} y={band.start * scale} width={width * scale} height={band.size * scale} fill="rgba(248,113,113,0.12)" />)}
    </svg>
  );
}

const field = 'w-full rounded border border-line bg-panel-2 px-1.5 py-1 text-[12px] tabular-nums';
/** 하단 툴바 메뉴 안의 설정. ToolbarMenu는 안쪽 클릭에 닫히므로 클릭을 여기서 멈춘다. */
export function LayoutGridSettings({ grid, onChange }: { grid: NoticeLayoutGrid; onChange: (grid: NoticeLayoutGrid) => void }) {
  const set = (patch: Partial<NoticeLayoutGrid>) => onChange(clampLayoutGrid({ ...grid, ...patch }));
  const num = (label: string, value: number, apply: (value: number) => void, name = label) => (
    <label className="flex min-w-0 flex-col gap-1 text-[11px] text-text-dim">{label}
      <input type="number" aria-label={`그리드 ${name}`} value={value} className={field}
        onChange={event => { const n = Number(event.target.value); if (Number.isFinite(n)) apply(n); }} />
    </label>
  );
  return (
    <div className="w-[248px] space-y-3 p-2 text-[12px] text-text" onClick={event => event.stopPropagation()}>
      <label className="flex items-center gap-2 font-medium">
        <input type="checkbox" checked={grid.visible} onChange={event => set({ visible: event.target.checked })} />그리드 보기
        <kbd className="ml-auto text-[11px] text-text-dim">Shift G</kbd>
      </label>
      <fieldset disabled={!grid.visible} className="space-y-2 disabled:opacity-50">
        {(['columns', 'rows'] as const).map(key => {
          const value: LayoutGridBand = grid[key], name = key === 'columns' ? '열' : '행', patch = (next: Partial<LayoutGridBand>) => set({ [key]: { ...value, ...next } });
          return <div key={key} className="space-y-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={value.on} onChange={event => patch({ on: event.target.checked })} />{name} 그리드</label>
            <div className="grid grid-cols-3 gap-2">
              {num(name, value.count, count => patch({ count }), name)}
              {num('간격', value.gutter, gutter => patch({ gutter }), `${name} 간격`)}
              {num('여백', value.margin, margin => patch({ margin }), `${name} 여백`)}
            </div>
          </div>;
        })}
        <label className="flex items-center gap-2"><input type="checkbox" checked={grid.square.on} onChange={event => set({ square: { ...grid.square, on: event.target.checked } })} />정사각 격자</label>
        <div className="grid grid-cols-3 gap-2">{num('격자 간격', grid.square.size, size => set({ square: { ...grid.square, size } }))}</div>
      </fieldset>
      <p className="text-[11px] leading-relaxed text-text-dim">그리드가 보일 때 Shift+드래그로 옮기면 가장 가까운 선에 붙어요. 값은 대지 px이며 이 브라우저에 기억돼요.</p>
    </div>
  );
}
